import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import {
  ApiError,
  downloadCertificatePdf,
  getCertificate,
  requestRevocation,
  type CertificateDetail,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { saveBlob } from "@/components/download";
import { formatDate } from "@/components/format";
import { ReissuePanel, ReissueTag } from "@/features/reissue/ReissuePanel";
import { DistributionPanel } from "./DistributionPanel";
import { VerificationPanel } from "./VerificationPanel";
import { DetailField, EmptyState, ErrorBanner, StatusBadge, ToolbarButton, WorkspaceHeader } from "@/components/ui";

const detailApi = getRouteApi("/staff/certificates/$certificateId");

export function CertificateDetailPage() {
  const { certificateId } = detailApi.useParams();
  const certificate = useQuery({
    queryKey: ["certificate", certificateId],
    queryFn: () => getCertificate(certificateId),
  });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (certificate.error instanceof ApiError && [404, 422].includes(certificate.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Certificate not found" detail="No certificate matches this link." />
        <Link to="/certificates" className="text-sm font-medium text-zinc-950 underline">
          Search certificates
        </Link>
      </section>
    );
  }
  if (!certificate.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Certificate" detail={certificateId} />
        <ErrorBanner error={certificate.error} />
        {certificate.isLoading ? <EmptyState title="Loading certificate" detail="Fetching details." /> : null}
      </section>
    );
  }
  return <CertificateView cert={certificate.data} />;
}

function CertificateView({ cert }: { cert: CertificateDetail }) {
  const { can } = useSession();
  const download = useMutation({
    mutationFn: async () => {
      saveBlob(await downloadCertificatePdf(cert.id), `${cert.doc_control_id.replaceAll("/", "-")}.pdf`);
    },
  });

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title={cert.doc_control_id}
        detail={cert.member_full_name}
        action={
          <div className="flex flex-wrap items-center gap-3">
            <ReissueTag cert={cert} />
            <StatusBadge status={cert.status} />
          </div>
        }
      />
      <ErrorBanner error={download.error} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.8fr)]">
        <div className="space-y-5">
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
              <DetailField
                label="Member"
                value={
                  can("member.read") ? (
                    <Link to="/members/$memberId" params={{ memberId: cert.member_id }} className="underline">
                      {cert.member_full_name}
                    </Link>
                  ) : (
                    cert.member_full_name
                  )
                }
              />
              <DetailField label="Membership no." value={cert.membership_no ?? "-"} mono />
              <DetailField label="Program" value={cert.program_name ?? "-"} />
              <DetailField label="Certificate type" value={cert.cert_type} />
              <DetailField label="Issued" value={formatDate(cert.issued_at)} />
              <DetailField label="Generated" value={formatDate(cert.generated_at ?? undefined)} />
              <DetailField label="PDF checksum" value={cert.pdf_checksum ?? "-"} mono />
              <DetailField label="Re-issue" value={cert.is_reissue ? "Yes" : "No"} />
              {cert.reissue_reason ? <DetailField label="Re-issue reason" value={cert.reissue_reason} /> : null}
            </dl>
            {cert.signatures.length > 0 ? (
              <div className="text-sm">
                <h3 className="text-xs font-medium text-zinc-500">Signatures</h3>
                <ul className="mt-1 space-y-0.5 text-zinc-900">
                  {cert.signatures.map((s) => (
                    <li key={s.slot}>
                      {s.slot} — {s.signatory_name}, version {s.version_no} (from {signatureDay(s.effective_from)})
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <ToolbarButton disabled={!cert.pdf_available || download.isPending} onClick={() => download.mutate()}>
                {download.isPending ? "Downloading..." : "Download PDF"}
              </ToolbarButton>
              {!cert.pdf_available ? <span className="text-xs text-zinc-500">The PDF has not been generated yet.</span> : null}
              {cert.batch_id ? (
                <Link
                  to="/batches/$batchId"
                  params={{ batchId: cert.batch_id }}
                  search={{ status: "all" }}
                  className="text-sm font-medium text-zinc-950 underline"
                >
                  Open batch
                </Link>
              ) : null}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <h2 className="text-sm font-semibold">Lineage</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {cert.lineage.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center justify-between gap-3">
                  {item.id === cert.id ? (
                    <span className="font-mono text-xs font-semibold">{item.doc_control_id} (this certificate)</span>
                  ) : (
                    <Link
                      to="/certificates/$certificateId"
                      params={{ certificateId: item.id }}
                      className="font-mono text-xs underline"
                    >
                      {item.doc_control_id}
                    </Link>
                  )}
                  <span className="flex items-center gap-2 text-xs text-zinc-500">
                    {item.is_reissue ? "Re-issue" : "Original"} · {formatDate(item.issued_at)}
                    <StatusBadge status={item.status} />
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <VerificationPanel certificateId={cert.id} />
          <DistributionPanel cert={cert} />
        </div>

        <div className="space-y-5">
          <RevocationPanel cert={cert} />
          <ReissuePanel cert={cert} />
        </div>
      </div>
    </section>
  );
}

function RevocationPanel({ cert }: { cert: CertificateDetail }) {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const latest = cert.latest_revocation;

  const submit = useMutation({
    mutationFn: () => requestRevocation(cert.id, reason.trim()),
    onSuccess: async () => {
      setReason("");
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["certificate", cert.id] });
      await queryClient.invalidateQueries({ queryKey: ["revocations"] });
    },
  });

  let body: React.ReactNode;
  if (cert.status === "revoked") {
    body = (
      <div className="space-y-2 text-sm">
        <p className="font-medium text-red-700">This certificate is revoked. Verification reports it as revoked.</p>
        {latest?.status === "approved" ? (
          <p className="text-zinc-600">
            Requested by {latest.requested_by_name}, approved by {latest.decided_by_name ?? "-"} on{" "}
            {formatDate(latest.decided_at ?? undefined)}. Reason: {latest.reason}
          </p>
        ) : null}
      </div>
    );
  } else if (latest?.status === "pending") {
    const mine = latest.requested_by === me?.user.id;
    body = (
      <div className="space-y-2 text-sm">
        <p className="font-medium text-amber-700">Revocation pending a second approver.</p>
        <p className="text-zinc-600">
          Requested by {latest.requested_by_name} on {formatDate(latest.created_at)}. Reason: {latest.reason}
        </p>
        {can("certificate.revoke.approve") && !mine ? (
          <Link
            to="/approvals/revocations"
            search={{ request: latest.id }}
            className="font-medium text-zinc-950 underline"
          >
            Review this request
          </Link>
        ) : null}
      </div>
    );
  } else if (cert.status !== "generated") {
    body = <p className="text-sm text-zinc-500">Only issued certificates can be revoked.</p>;
  } else if (!can("certificate.revoke")) {
    body = <p className="text-sm text-zinc-500">The certificate is valid.</p>;
  } else if (!open) {
    body = (
      <div className="space-y-3 text-sm">
        {latest?.status === "rejected" ? (
          <p className="text-zinc-600">
            The last request was rejected by {latest.decided_by_name ?? "-"}: {latest.decision_comment}
          </p>
        ) : null}
        <ToolbarButton tone="danger" onClick={() => setOpen(true)}>
          Request revocation
        </ToolbarButton>
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Reason for revocation</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            className="mt-1 w-full resize-none rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </label>
        <p className="text-xs text-zinc-500">
          A second person must approve before the certificate is revoked. Revocation cannot be undone.
        </p>
        <div className="flex gap-2">
          <ToolbarButton
            tone="danger"
            disabled={reason.trim() === "" || submit.isPending}
            onClick={() => submit.mutate()}
          >
            Submit request
          </ToolbarButton>
          <ToolbarButton disabled={submit.isPending} onClick={() => setOpen(false)}>
            Cancel
          </ToolbarButton>
        </div>
      </div>
    );
  }

  return (
    <aside className="h-fit space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Revocation</h2>
      <ErrorBanner error={submit.error} />
      {body}
    </aside>
  );
}

// A version's start as "1 Jan 2025" in Lagos, the way the API's messages print dates.
function signatureDay(iso: string) {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" }).format(new Date(iso));
}
